// Copyright 2025, University of Maryland, All Rights Reserved
import { axiosInstance } from './client';

// API functions for the unified S3-based configuration management
export const configApi = {
  // Default endpoints (system defaults) - omit org_id/ws_id for default configs
  async getDefaultTemplates(): Promise<string[]> {
    const response = await axiosInstance.get('/promptconfig/', {
      params: { config_type: 'template' }
    });
    return response.data;
  },

  async getDefaultTemplate(name: string): Promise<any> {
    const response = await axiosInstance.get(`/promptconfig/${name}`, {
      params: { config_type: 'template' }
    });
    return response.data;
  },

  async createDefaultTemplate(name: string, content: Record<string, any>): Promise<any> {
    const response = await axiosInstance.post('/promptconfig/', {
      name,
      content
    }, {
      params: { config_type: 'template' }
    });
    return response.data;
  },

  async updateDefaultTemplate(name: string, content: Record<string, any>): Promise<any> {
    const response = await axiosInstance.put(`/promptconfig/${name}`, {
      name,
      content
    }, {
      params: { config_type: 'template' }
    });
    return response.data;
  },

  async deleteDefaultTemplate(name: string): Promise<any> {
    const response = await axiosInstance.delete(`/promptconfig/${name}`, {
      params: { config_type: 'template' }
    });
    return response.data;
  },

  async getDefaultPrompts(analysisType?: string): Promise<string[]> {
    const response = await axiosInstance.get('/promptconfig/', {
      params: { config_type: 'prompt', analysis_type: analysisType }
    });
    return response.data;
  },

  async getDefaultPrompt(name: string, analysisType?: string): Promise<any> {
    // Convert analysis type to lowercase for S3 folder consistency
    const normalizedAnalysisType = analysisType ? analysisType.toLowerCase().trim() : undefined;
    
    const response = await axiosInstance.get(`/promptconfig/${name}`, {
      params: { config_type: 'prompt', analysis_type: normalizedAnalysisType }
    });
    return response.data;
  },

  async createDefaultPrompt(name: string, content: string, analysisType?: string): Promise<any> {
    // Analysis type is required for prompts
    if (!analysisType) {
      throw new Error('analysis_type is required for prompts');
    }
    // Convert analysis type to lowercase for S3 folder consistency
    const normalizedAnalysisType = analysisType.toLowerCase().trim();
    
    const response = await axiosInstance.post('/promptconfig/', {
      name,
      content
    }, {
      params: { config_type: 'prompt', analysis_type: normalizedAnalysisType }
    });
    return response.data;
  },

  async updateDefaultPrompt(name: string, content: string, analysisType?: string): Promise<any> {
    // Analysis type is required for prompts
    if (!analysisType) {
      throw new Error('analysis_type is required for prompts');
    }
    // Convert analysis type to lowercase for S3 folder consistency
    const normalizedAnalysisType = analysisType.toLowerCase().trim();
    
    const response = await axiosInstance.put(`/promptconfig/${name}`, {
      name,
      content
    }, {
      params: { config_type: 'prompt', analysis_type: normalizedAnalysisType }
    });
    return response.data;
  },

  async deleteDefaultPrompt(name: string, analysisType?: string): Promise<any> {
    // Analysis type is required for prompts
    if (!analysisType) {
      throw new Error('analysis_type is required for prompts');
    }
    // Convert analysis type to lowercase for S3 folder consistency
    const normalizedAnalysisType = analysisType.toLowerCase().trim();
    
    const response = await axiosInstance.delete(`/promptconfig/${name}`, {
      params: { config_type: 'prompt', analysis_type: normalizedAnalysisType }
    });
    return response.data;
  },

  // Workspace endpoints (with org_id, ws_id parameters) - include org_id/ws_id for workspace configs
  async getWorkspaceTemplates(orgId: string, wsId: string): Promise<string[]> {
    const response = await axiosInstance.get('/promptconfig/', {
      params: { config_type: 'template', org_id: orgId, ws_id: wsId }
    });
    return response.data;
  },

  async getWorkspaceTemplate(name: string, orgId: string, wsId: string): Promise<any> {
    const response = await axiosInstance.get(`/promptconfig/${name}`, {
      params: { config_type: 'template', org_id: orgId, ws_id: wsId }
    });
    return response.data;
  },

  async createWorkspaceTemplate(name: string, content: Record<string, any>, orgId: string, wsId: string): Promise<any> {
    const response = await axiosInstance.post('/promptconfig/', {
      name,
      content
    }, {
      params: { config_type: 'template', org_id: orgId, ws_id: wsId }
    });
    return response.data;
  },

  async updateWorkspaceTemplate(name: string, content: Record<string, any>, orgId: string, wsId: string): Promise<any> {
    const response = await axiosInstance.put(`/promptconfig/${name}`, {
      name,
      content
    }, {
      params: { config_type: 'template', org_id: orgId, ws_id: wsId }
    });
    return response.data;
  },

  async deleteWorkspaceTemplate(name: string, orgId: string, wsId: string): Promise<any> {
    const response = await axiosInstance.delete(`/promptconfig/${name}`, {
      params: { config_type: 'template', org_id: orgId, ws_id: wsId }
    });
    return response.data;
  },

  async getWorkspacePrompts(orgId: string, wsId: string, analysisType?: string): Promise<string[]> {
    const response = await axiosInstance.get('/promptconfig/', {
      params: { config_type: 'prompt', org_id: orgId, ws_id: wsId, analysis_type: analysisType }
    });
    return response.data;
  },

  async getWorkspacePrompt(name: string, orgId: string, wsId: string, analysisType?: string): Promise<any> {
    // Convert analysis type to lowercase for S3 folder consistency
    const normalizedAnalysisType = analysisType ? analysisType.toLowerCase().trim() : undefined;
    
    const response = await axiosInstance.get(`/promptconfig/${name}`, {
      params: { config_type: 'prompt', org_id: orgId, ws_id: wsId, analysis_type: normalizedAnalysisType }
    });
    return response.data;
  },

  async createWorkspacePrompt(name: string, content: string, orgId: string, wsId: string, analysisType?: string): Promise<any> {
    // Analysis type is required for prompts
    if (!analysisType) {
      throw new Error('analysis_type is required for prompts');
    }
    // Convert analysis type to lowercase for S3 folder consistency
    const normalizedAnalysisType = analysisType.toLowerCase().trim();
    
    const response = await axiosInstance.post('/promptconfig/', {
      name,
      content
    }, {
      params: { config_type: 'prompt', org_id: orgId, ws_id: wsId, analysis_type: normalizedAnalysisType }
    });
    return response.data;
  },

  async updateWorkspacePrompt(name: string, content: string, orgId: string, wsId: string, analysisType?: string): Promise<any> {
    // Analysis type is required for prompts
    if (!analysisType) {
      throw new Error('analysis_type is required for prompts');
    }
    // Convert analysis type to lowercase for S3 folder consistency
    const normalizedAnalysisType = analysisType.toLowerCase().trim();
    
    const response = await axiosInstance.put(`/promptconfig/${name}`, {
      name,
      content
    }, {
      params: { config_type: 'prompt', org_id: orgId, ws_id: wsId, analysis_type: normalizedAnalysisType }
    });
    return response.data;
  },

  async deleteWorkspacePrompt(name: string, orgId: string, wsId: string, analysisType?: string): Promise<any> {
    // Analysis type is required for prompts
    if (!analysisType) {
      throw new Error('analysis_type is required for prompts');
    }
    // Convert analysis type to lowercase for S3 folder consistency
    const normalizedAnalysisType = analysisType.toLowerCase().trim();
    
    const response = await axiosInstance.delete(`/promptconfig/${name}`, {
      params: { config_type: 'prompt', org_id: orgId, ws_id: wsId, analysis_type: normalizedAnalysisType }
    });
    return response.data;
  },

  // Migration endpoint (default configs only)
  async migrateConfigs(): Promise<any> {
    const response = await axiosInstance.post('/promptconfig/migrate');
    return response.data;
  },

  // Workspace configuration endpoints
  async getWorkspaceConfig(orgId: string, wsId: string): Promise<any> {
    const response = await axiosInstance.get('/workspace-config', {
      params: { org_id: orgId, ws_id: wsId }
    });
    return response.data;
  },

  async updateWorkspaceConfig(orgId: string, wsId: string, updates: { default_llm_model?: string }): Promise<any> {
    const response = await axiosInstance.patch('/workspace-config', updates, {
      params: { org_id: orgId, ws_id: wsId }
    });
    return response.data;
  },

  // Unified API methods that work with different levels
  async getTemplates(level: 'default' | 'workspace', orgId?: string, wsId?: string): Promise<string[]> {
    switch (level) {
      case 'default':
        return this.getDefaultTemplates();
      case 'workspace':
        if (!orgId || !wsId) {
          throw new Error('orgId and wsId are required for workspace operations');
        }
        return this.getWorkspaceTemplates(orgId, wsId);
      default:
        return this.getDefaultTemplates();
    }
  },

  async getTemplate(level: 'default' | 'workspace', name: string, orgId?: string, wsId?: string): Promise<any> {
    switch (level) {
      case 'default':
        return this.getDefaultTemplate(name);
      case 'workspace':
        if (!orgId || !wsId) {
          throw new Error('orgId and wsId are required for workspace operations');
        }
        return this.getWorkspaceTemplate(name, orgId, wsId);
      default:
        return this.getDefaultTemplate(name);
    }
  },

  async createTemplate(level: 'default' | 'workspace', name: string, content: Record<string, any>, orgId?: string, wsId?: string): Promise<any> {
    switch (level) {
      case 'default':
        return this.createDefaultTemplate(name, content);
      case 'workspace':
        if (!orgId || !wsId) {
          throw new Error('orgId and wsId are required for workspace operations');
        }
        return this.createWorkspaceTemplate(name, content, orgId, wsId);
      default:
        return this.createDefaultTemplate(name, content);
    }
  },

  async updateTemplate(level: 'default' | 'workspace', name: string, content: Record<string, any>, orgId?: string, wsId?: string): Promise<any> {
    switch (level) {
      case 'default':
        return this.updateDefaultTemplate(name, content);
      case 'workspace':
        if (!orgId || !wsId) {
          throw new Error('orgId and wsId are required for workspace operations');
        }
        return this.updateWorkspaceTemplate(name, content, orgId, wsId);
      default:
        return this.updateDefaultTemplate(name, content);
    }
  },

  async deleteTemplate(level: 'default' | 'workspace', name: string, orgId?: string, wsId?: string): Promise<any> {
    switch (level) {
      case 'default':
        return this.deleteDefaultTemplate(name);
      case 'workspace':
        if (!orgId || !wsId) {
          throw new Error('orgId and wsId are required for workspace operations');
        }
        return this.deleteWorkspaceTemplate(name, orgId, wsId);
      default:
        return this.deleteDefaultTemplate(name);
    }
  },

  async getPrompts(level: 'default' | 'workspace', orgId?: string, wsId?: string, analysisType?: string): Promise<string[]> {
    switch (level) {
      case 'default':
        return this.getDefaultPrompts(analysisType);
      case 'workspace':
        if (!orgId || !wsId) {
          throw new Error('orgId and wsId are required for workspace operations');
        }
        return this.getWorkspacePrompts(orgId, wsId, analysisType);
      default:
        return this.getDefaultPrompts(analysisType);
    }
  },

  async getPrompt(level: 'default' | 'workspace', name: string, orgId?: string, wsId?: string, analysisType?: string): Promise<any> {
    switch (level) {
      case 'default':
        return this.getDefaultPrompt(name, analysisType);
      case 'workspace':
        if (!orgId || !wsId) {
          throw new Error('orgId and wsId are required for workspace operations');
        }
        return this.getWorkspacePrompt(name, orgId, wsId, analysisType);
      default:
        return this.getDefaultPrompt(name, analysisType);
    }
  },

  async createPrompt(level: 'default' | 'workspace', name: string, content: string, orgId?: string, wsId?: string, analysisType?: string): Promise<any> {
    switch (level) {
      case 'default':
        return this.createDefaultPrompt(name, content, analysisType);
      case 'workspace':
        if (!orgId || !wsId) {
          throw new Error('orgId and wsId are required for workspace operations');
        }
        return this.createWorkspacePrompt(name, content, orgId, wsId, analysisType);
      default:
        return this.createDefaultPrompt(name, content, analysisType);
    }
  },

  async updatePrompt(level: 'default' | 'workspace', name: string, content: string, orgId?: string, wsId?: string, analysisType?: string): Promise<any> {
    switch (level) {
      case 'default':
        return this.updateDefaultPrompt(name, content, analysisType);
      case 'workspace':
        if (!orgId || !wsId) {
          throw new Error('orgId and wsId are required for workspace operations');
        }
        return this.updateWorkspacePrompt(name, content, orgId, wsId, analysisType);
      default:
        return this.updateDefaultPrompt(name, content, analysisType);
    }
  },

  async deletePrompt(level: 'default' | 'workspace', name: string, orgId?: string, wsId?: string, analysisType?: string): Promise<any> {
    switch (level) {
      case 'default':
        return this.deleteDefaultPrompt(name, analysisType);
      case 'workspace':
        if (!orgId || !wsId) {
          throw new Error('orgId and wsId are required for workspace operations');
        }
        return this.deleteWorkspacePrompt(name, orgId, wsId, analysisType);
      default:
        return this.deleteDefaultPrompt(name, analysisType);
    }
  }
};